/**
 * Client text-to-speech for auto-host voice mode (#91, D2).
 *
 * The auto-host reads each clue aloud in the browser (zero server cost/latency).
 * Reading is pause-aware: a clue arrives as {@link SpeechSegment}s — chunks of text
 * with a dramatic beat after each — and this wrapper speaks each chunk as its own
 * utterance, awaiting the encoded pause between them so the delivery lands like a
 * game show, not a monotone dump.
 *
 * It sits behind the small {@link SpeechSynthesizer} interface so a future backend
 * voice (Polly) is an additive swap, not a rewrite of the pages. The browser impl
 * feature-detects `window.speechSynthesis`; where it is absent (older browsers,
 * jsdom under test) {@link WebSpeechSynthesizer.isSupported} is false and
 * `speakClue` resolves immediately — no client is ever blocked (the server still
 * opens the buzzer on its own duration estimate, §5.3).
 */

import type { SpeechSegment } from '@jeopardy/shared';
import { isBackendTtsEnabled, registerVoiceSingletonReset } from './voice-config';
import { PollySynthesizer } from './polly-synth';

/** The seam every page reads TTS through (browser today, backend-swappable). */
export interface SpeechSynthesizer {
  /** True iff speech synthesis is available in this environment. */
  isSupported(): boolean;
  /**
   * Read the segments aloud, awaiting each segment's `pauseMsAfter` between them.
   * Resolves when the last segment finishes (or immediately when unsupported).
   */
  speakClue(segments: SpeechSegment[]): Promise<void>;
  /**
   * Read a clue from a pre-generated audio URL (#99, NFR-6.9): fetch + play the
   * cached MP3 directly, skipping the on-demand `/api/voice/tts` synthesis. Any
   * failure (fetch/HTTP/playback) falls back to {@link speakClue} with the given
   * segments — which itself falls back to the browser voice — so a missing or bad
   * clip never breaks the read. The browser synthesizer has no URL notion, so it
   * simply speaks the segments.
   */
  speakClueFromUrl(url: string, fallbackSegments: SpeechSegment[]): Promise<void>;
  /** Cancel any in-progress or queued speech. */
  cancel(): void;
  /**
   * Prime the engine from within a user gesture (#91). iOS Safari blocks
   * programmatic `speak()` until speech has been started by a tap, so the pages
   * call this on the first interaction (join / Start). No-op where unneeded.
   */
  unlock(): void;
}

/** Structural subset of `SpeechSynthesisVoice` we read (keeps the impl injectable). */
export interface SynthVoiceLike {
  name: string;
  lang: string;
  default: boolean;
  /**
   * True for an on-device voice, false for a (usually higher-quality) network voice.
   * Optional — absent on the structural test doubles. We prefer network voices.
   */
  localService?: boolean;
}

/** Structural subset of `SpeechSynthesisUtterance` we drive. */
export interface UtteranceLike {
  text: string;
  rate: number;
  pitch: number;
  voice: SynthVoiceLike | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
}

/** Structural subset of `window.speechSynthesis` we call. */
export interface SpeechSynthesisLike {
  speak(utterance: UtteranceLike): void;
  cancel(): void;
  getVoices(): SynthVoiceLike[];
}

export type UtteranceFactory = (text: string) => UtteranceLike;

/**
 * Voice-name fragments preferred for the game-show read, most-preferred first
 * (matched case-insensitively as substrings, so "Samantha (Enhanced)" matches
 * "samantha"). These favor the natural / neural voices across platforms —
 * Chrome's network "Google" voices, Windows "Natural" voices, and Apple's
 * enhanced/premium voices. Failing all of these we prefer any NETWORK `en-*`
 * voice (higher quality than the on-device "compact" ones), then any `en-*`, then
 * the platform default (leaving `voice` null lets the engine choose). Voice
 * quality is ultimately capped by the OS — the follow-up is neural backend TTS.
 */
const PREFERRED_VOICE_FRAGMENTS = [
  'google us english',
  'natural', // Microsoft "… (Natural) …" / Apple enhanced markers
  'premium',
  'enhanced',
  'ava',
  'samantha',
  'siri',
  'alex',
  'daniel',
];

/** Game-show delivery: a touch slower and lower than default (per the issue sketch). */
const DEFAULT_RATE = 0.9;
const DEFAULT_PITCH = 0.88;

export interface WebSpeechSynthesizerOptions {
  /** The synthesis engine. Defaults to `globalThis.speechSynthesis`. */
  synth?: SpeechSynthesisLike | null;
  /** Utterance constructor. Defaults to `new SpeechSynthesisUtterance(text)`. */
  createUtterance?: UtteranceFactory;
  /** Pause between segments. Defaults to a real `setTimeout` sleep. Injectable for tests. */
  sleep?: (ms: number) => Promise<void>;
  rate?: number;
  pitch?: number;
}

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, Math.max(0, ms)));
}

function resolveGlobalSynth(): SpeechSynthesisLike | null {
  const g = globalThis as { speechSynthesis?: SpeechSynthesisLike };
  return g.speechSynthesis ?? null;
}

function defaultCreateUtterance(text: string): UtteranceLike {
  const Ctor = (
    globalThis as { SpeechSynthesisUtterance?: new (t: string) => UtteranceLike }
  ).SpeechSynthesisUtterance;
  if (!Ctor) throw new Error('SpeechSynthesisUtterance unavailable');
  return new Ctor(text);
}

/** Browser {@link SpeechSynthesizer} over `window.speechSynthesis` (D2). */
export class WebSpeechSynthesizer implements SpeechSynthesizer {
  private readonly synth: SpeechSynthesisLike | null;
  private readonly createUtterance: UtteranceFactory;
  private readonly sleep: (ms: number) => Promise<void>;
  private readonly rate: number;
  private readonly pitch: number;
  /** Serialize speakClue calls so a new clue cancels a lagging previous read. */
  private generation = 0;

  constructor(opts: WebSpeechSynthesizerOptions = {}) {
    this.synth = opts.synth !== undefined ? opts.synth : resolveGlobalSynth();
    this.createUtterance = opts.createUtterance ?? defaultCreateUtterance;
    this.sleep = opts.sleep ?? defaultSleep;
    this.rate = opts.rate ?? DEFAULT_RATE;
    this.pitch = opts.pitch ?? DEFAULT_PITCH;
  }

  isSupported(): boolean {
    return this.synth !== null;
  }

  /**
   * Pick the most natural installed voice: a preferred (natural/neural) name
   * fragment first, then any NETWORK `en-*` voice (higher quality than on-device
   * "compact" voices), then any `en-*`, else null (engine default).
   */
  private pickVoice(): SynthVoiceLike | null {
    if (!this.synth) return null;
    const voices = this.synth.getVoices();
    if (voices.length === 0) return null;
    const isEnglish = (v: SynthVoiceLike): boolean =>
      v.lang.toLowerCase().startsWith('en');
    for (const fragment of PREFERRED_VOICE_FRAGMENTS) {
      const match = voices.find(
        (v) => isEnglish(v) && v.name.toLowerCase().includes(fragment),
      );
      if (match) return match;
    }
    // Network voices (localService === false) are noticeably better than the
    // default on-device compact voices where available (e.g. Chrome).
    const networkEn = voices.find((v) => isEnglish(v) && v.localService === false);
    if (networkEn) return networkEn;
    return voices.find(isEnglish) ?? null;
  }

  async speakClue(segments: SpeechSegment[]): Promise<void> {
    const synth = this.synth;
    if (!synth) return; // unsupported → resolve immediately (§5.3)

    // A newer read supersedes any in-flight one.
    const gen = ++this.generation;
    synth.cancel();
    const voice = this.pickVoice();

    for (let i = 0; i < segments.length; i++) {
      if (gen !== this.generation) return; // superseded/cancelled
      const segment = segments[i];
      if (!segment) continue;
      await this.speakOne(synth, segment.text, voice);
      if (gen !== this.generation) return;
      const isLast = i === segments.length - 1;
      if (!isLast && segment.pauseMsAfter > 0) {
        await this.sleep(segment.pauseMsAfter);
      }
    }
  }

  /**
   * The browser engine has no notion of a pre-generated audio URL (#99); it just
   * reads the fallback segments aloud. (The backend {@link PollySynthesizer}
   * overrides this to fetch + play the cached MP3.)
   */
  speakClueFromUrl(_url: string, fallbackSegments: SpeechSegment[]): Promise<void> {
    return this.speakClue(fallbackSegments);
  }

  private speakOne(
    synth: SpeechSynthesisLike,
    text: string,
    voice: SynthVoiceLike | null,
  ): Promise<void> {
    return new Promise<void>((resolve) => {
      // Empty chunk: nothing to say (some engines never fire onend for "").
      if (text.trim() === '') {
        resolve();
        return;
      }
      const utterance = this.createUtterance(text);
      utterance.rate = this.rate;
      utterance.pitch = this.pitch;
      utterance.voice = voice;
      // Resolve on either end or error so a failed utterance never hangs the read.
      utterance.onend = () => resolve();
      utterance.onerror = () => resolve();
      synth.speak(utterance);
    });
  }

  cancel(): void {
    this.generation++;
    this.synth?.cancel();
  }

  /**
   * Prime the engine within a user gesture so iOS Safari will allow the later
   * programmatic clue reads (#91). Speaks a single near-empty utterance and warms
   * the voice list; harmless where not required. Never throws.
   */
  private unlocked = false;
  unlock(): void {
    if (this.unlocked || !this.synth) return;
    this.unlocked = true;
    try {
      // Warm the (async) voice list, then speak a whitespace utterance to satisfy
      // iOS's "speech must start from a gesture" rule for subsequent reads.
      this.synth.getVoices();
      const u = this.createUtterance(' ');
      u.rate = this.rate;
      u.pitch = this.pitch;
      u.voice = null;
      u.onend = null;
      u.onerror = null;
      this.synth.speak(u);
    } catch {
      // Best-effort priming — a failure here just means iOS may stay muted; the
      // server still opens the buzzer on its estimate (§5.3), so play continues.
    }
  }
}

let sharedSynthesizer: SpeechSynthesizer | null = null;
let backendSynthesizer: SpeechSynthesizer | null = null;

// A voice-config change (config fetch / test reset) drops the cached singletons so
// the next `get` re-selects the correct adapter. Registered here (rather than
// voice-config importing this module) to keep the dependency one-directional.
registerVoiceSingletonReset(() => {
  sharedSynthesizer = null;
  backendSynthesizer = null;
});

/**
 * The process-wide synthesizer (one queue for the whole tab). Returns the backend
 * {@link PollySynthesizer} (with a browser fallback wired in, D-FALL) when the
 * deployment advertised backend TTS (#93, D-SEAM), else the browser
 * {@link WebSpeechSynthesizer}.
 */
export function getSpeechSynthesizer(): SpeechSynthesizer {
  if (isBackendTtsEnabled()) {
    if (!backendSynthesizer) {
      backendSynthesizer = new PollySynthesizer({ fallback: getWebSpeechSynthesizer() });
      installGestureUnlock(backendSynthesizer);
    }
    return backendSynthesizer;
  }
  return getWebSpeechSynthesizer();
}

function getWebSpeechSynthesizer(): SpeechSynthesizer {
  if (!sharedSynthesizer) {
    sharedSynthesizer = new WebSpeechSynthesizer();
    installGestureUnlock(sharedSynthesizer);
  }
  return sharedSynthesizer;
}

/**
 * Install a ONE-SHOT listener that primes the synthesizer on the first user
 * gesture (#91). iOS Safari blocks programmatic `speak()` until speech has been
 * started from a tap, and the auto-host reads clues programmatically — so we unlock
 * on the first `pointerdown`/`touchend`/`keydown` anywhere, then remove the
 * listeners. Guarded for non-browser/test environments.
 */
function installGestureUnlock(synth: SpeechSynthesizer): void {
  const w = globalThis as {
    addEventListener?: (t: string, cb: () => void, o?: unknown) => void;
    removeEventListener?: (t: string, cb: () => void, o?: unknown) => void;
  };
  if (typeof w.addEventListener !== 'function') return;
  const events = ['pointerdown', 'touchend', 'keydown'];
  const onGesture = (): void => {
    synth.unlock();
    for (const e of events) w.removeEventListener?.(e, onGesture);
  };
  for (const e of events) w.addEventListener?.(e, onGesture, { once: true });
}
